import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import PosterCard, { PosterTile, Countdown, DIVISION_META } from "@/components/Studio";
import { NotifyForm } from "@/components/ProductCard";
import NotFound from "../NotFound";

export default function Division() {
  const { division } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setMissing(false);
    api.get(`/public/cinema/division/${division}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [division]);

  if (missing) return <NotFound />;
  const meta = DIVISION_META[division];
  const isPremieres = division === "premieres";
  const isReels = division === "reels";
  const videos = isPremieres ? [] : data?.videos || [];

  return (
    <div className="bg-ink min-h-screen" data-testid={`division-${division}`}>
      <Seo title={`${meta?.title || "Cinema"} — Dirty Church Chronicles Studios`}
        description={meta?.desc || "Dirty Church Chronicles Studios."} path={`/cinema/${division}`} />
      <section className="pt-36 pb-14 max-w-7xl mx-auto px-6 sm:px-10">
        <Link to="/cinema" data-testid="back-to-cinema"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-paper/50 hover:text-paper transition-colors mb-8">
          <ArrowLeft size={13} /> The Cinema
        </Link>
        <Reveal>
          <p className="eyebrow mb-3">Dirty Church Chronicles Studios</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4" data-testid="division-title">
            {meta?.title}
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl">{meta?.desc}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28">
        {isPremieres ? (
          <>
            {(data?.upcoming || []).length === 0 && (
              <p className="font-ui text-sm text-paper/45 border border-paper/10 p-10" data-testid="premieres-empty">
                No premieres are currently scheduled. Confirmed releases will appear here with countdowns.
              </p>
            )}
            <div className="space-y-6">
              {(data?.upcoming || []).map((v) => (
                <Reveal key={v.video_id}>
                  <div className="border border-gold/25 bg-coal p-8 flex flex-col sm:flex-row gap-8 items-start" data-testid={`premiere-${v.slug}`}>
                    <Link to={`/cinema/watch/${v.slug}`} className="w-full sm:w-64 shrink-0 aspect-video overflow-hidden border border-paper/10 bg-black">
                      <PosterTile video={v} />
                    </Link>
                    <div className="flex-1">
                      <p className="eyebrow mb-3">{DIVISION_META[v.division]?.title || v.division}</p>
                      <h3 className="font-display text-2xl sm:text-3xl text-paper mb-3">{v.title}</h3>
                      {v.logline && <p className="font-reading text-lg text-paper/65 mb-6">{v.logline}</p>}
                      <Countdown releaseAt={v.release_at} timezone={v.timezone} />
                      <div className="mt-6 max-w-md">
                        <NotifyForm product={{ product_id: `video:${v.slug}` }} compact />
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            {(data?.recent || []).length > 0 && (
              <div className="mt-16">
                <p className="eyebrow mb-6">Recently Premiered</p>
                <div className="flex gap-5 overflow-x-auto pb-4">
                  {data.recent.map((v) => <PosterCard key={v.video_id} video={v} />)}
                </div>
              </div>
            )}
          </>
        ) : videos.length === 0 && data ? (
          <p className="font-ui text-sm text-paper/45 border border-paper/10 p-10" data-testid="division-empty">
            This division is in production. Confirmed {meta?.title?.toLowerCase()} will appear here — join the
            reader list to hear first.
          </p>
        ) : (
          <div className={isReels
            ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"}>
            {videos.map((v, i) => (
              <Reveal key={v.video_id} delay={i * 0.06}>
                <PosterCard video={v} vertical={isReels} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
